/*
 * The "@" source (spec 12 §3.5, §4.4; spec 01 §11): the people who match, best match first, then
 * the mentionable groups of the `groups` slice — each group as a group item, so the list renders
 * it as a chip. The slices are read at query time, never captured, so the source follows the store
 * without being rebuilt on every event.
 *
 * The order is what makes Enter pick the right person (A-11 item 6): a handle that is exactly the
 * term beats a handle that starts with it, which beats a name whose word starts with it — so `@be`
 * offers Bea before "Anna Beispiel" — and among equals the conversation's members come first. The
 * person typing is never offered to themselves.
 *
 * Somebody whose invitation nobody has answered yet belongs in the list: the mention is legal and the
 * account is real, and what the reader needs to know is that it may lie unread until the invitation is
 * answered. That is the `badge` on the suggestion — the warning pill the directory and the picker draw
 * beside the same person.
 */

import { groupName } from "../../groups/group.ts"
import { t } from "../../i18n/index.ts"
import { isGuest, standingLabel } from "../../people/standing.ts"
import type { PublicUser } from "../../store/model.ts"
import type { AutocompleteItem, AutocompleteSource } from "../types.ts"

export interface PersonLike {
	id: number
	handle: string
	displayName: string
	/**
	 * "active", "invited", "deactivated". A deactivated account is not offered — the server refuses
	 * the mention (`error.account.notMentionable`) and offering it would be offering a refusal —
	 * while an invited one is: the person exists, the mention is legal, and what the reader needs to
	 * know is that it may lie unread until the invitation is answered.
	 */
	state?: string
	/** The account's kind and role, from which its standing is read: a guest is named so. */
	kind?: PublicUser["kind"]
	role?: PublicUser["role"]
}

export interface GroupLike {
	id: number
	handle: string
	displayName: string
	mentionable: boolean
	memberIds: number[]
}

export interface PeopleSlices {
	users: Record<number, PersonLike>
	groups: Record<number, GroupLike>
	/** The current conversation's members; empty outside a conversation. */
	memberIds: readonly number[]
	/** The account that is typing, who is never offered to themselves; absent means nobody is. */
	meId?: number | undefined
}

/** How well a term matches, smallest first: the exact handle, a handle prefix, a name-word prefix. */
function quality(term: string, handle: string, displayName: string): number {
	if (term === "") return 0
	const needle = term.toLowerCase()
	const h = handle.toLowerCase()
	if (h === needle) return 0
	if (h.startsWith(needle)) return 1
	return displayName
		.toLowerCase()
		.split(/\s+/)
		.some((word) => word.startsWith(needle))
		? 2
		: -1
}

/** The one shipped locale, for the alphabet the ties are broken in (core/i18n/format.ts). */
const byName = (a: { displayName: string }, b: { displayName: string }) =>
	a.displayName.localeCompare(b.displayName, "en")

export function peopleSource(read: () => PeopleSlices): AutocompleteSource {
	return {
		trigger: "@",
		minChars: 0,
		query(term) {
			const { users, groups, memberIds, meId } = read()
			const members = new Set(memberIds)
			const people = Object.values(users)
				// A bot — Claude, whom AI chats act as, or a machine's account — is no one a mention
				// reaches: it has no mailbox, and Claude is in no people picker (A3-D11).
				.filter((u) => u.id !== meId && u.state !== "deactivated" && u.kind !== "bot")
				.map((u) => ({ user: u, quality: quality(term, u.handle, u.displayName) }))
				.filter((ranked) => ranked.quality !== -1)
				.sort(
					(a, b) =>
						a.quality - b.quality ||
						Number(members.has(b.user.id)) - Number(members.has(a.user.id)) ||
						byName(a.user, b.user),
				)
			const mentionable = Object.values(groups)
				.filter((g) => g.mentionable && quality(term, g.handle, groupName(g)) !== -1)
				.sort(byName)
			const items: AutocompleteItem[] = [
				...people.map(({ user: u }) => ({
					id: `u:${u.id}`,
					label: u.displayName,
					detail: `@${u.handle}`,
					kind: "user" as const,
					...badgeOf(u),
				})),
				...mentionable.map((g) => ({
					id: `g:${g.id}`,
					label: groupName(g),
					detail: `@${g.handle}`,
					kind: "group" as const,
					memberCount: g.memberIds.length,
				})),
			]
			return Promise.resolve(items)
		},
		insert(item) {
			return `@${item.detail?.slice(1) ?? ""}`
		},
	}
}

/**
 * The pill of a suggested person: *Invited* warns that the mention reaches nobody yet; *Guest* says
 * the person is outside the team, before anything the team says among itself is written to them.
 */
function badgeOf(u: PersonLike): Pick<AutocompleteItem, "badge" | "badgeTone"> {
	if (u.state === "invited") return { badge: t("people.state.invited"), badgeTone: "warning" }
	if (u.kind === undefined || u.role === undefined) return {}
	return isGuest({ kind: u.kind, role: u.role }) ? { badge: standingLabel("guest") } : {}
}
